import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import NewsletterForm from '../components/NewsletterForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${slug}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-detail-loading">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-detail-not-found">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Article not found</p>
          <Link to="/blogs" className="text-secondary font-semibold">
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title={`${blog.title} | Vocavi Partners Insights`}
        description={blog.excerpt || blog.title}
        keywords={`${blog.category}, executive search, leadership insights, ${blog.title.toLowerCase()}`}
        image={blog.image_url || 'https://vocavi.in/og-image.png'}
        canonical={`https://vocavi.in/blogs/${blog.slug}`}
        type="article"
        author={blog.author}
      />
      {/* Back Button */}
      <div className="pt-24 pb-8 bg-white sticky top-20 z-40 border-b" data-testid="blog-detail-header">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blogs"
            data-testid="back-to-blogs"
            className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Insights
          </Link>
        </div>
      </div>

      {/* Blog Content */}
      <article className="section-spacing bg-white" data-testid="blog-detail-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="mb-4">
              <span className="bg-secondary text-primary px-4 py-2 text-xs font-semibold uppercase tracking-wider">
                {blog.category}
              </span>
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold text-primary mb-6" data-testid="blog-title">
              {blog.title}
            </h1>

            <div className="flex items-center gap-6 text-muted-foreground mb-8" data-testid="blog-meta">
              <span className="flex items-center">
                <Calendar size={18} className="mr-2" />
                {format(new Date(blog.created_at), 'MMMM dd, yyyy')}
              </span>
              <span className="flex items-center">
                <User size={18} className="mr-2" />
                {blog.author}
              </span>
            </div>

            {blog.image_url && (
              <div className="mb-12">
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  className="w-full h-96 object-cover shadow-xl"
                />
              </div>
            )}

            <div
              className="prose prose-lg max-w-none text-muted-foreground"
              data-testid="blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            <div className="mt-16 border-t border-slate-200 pt-10" data-testid="blog-detail-newsletter">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Enjoyed this article?</p>
              <h3 className="text-xl md:text-2xl font-bold text-primary mb-4">Get leadership hiring insights in your inbox</h3>
              <NewsletterForm source="blog" variant="light" testId="blog-detail-newsletter-form" />
            </div>
          </motion.div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="section-spacing bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary mb-4">
            Looking for Leadership Talent?
          </h2>
          <p className="text-muted-foreground mb-6">
            Connect with Vocavi Partners to discuss your executive search needs.
          </p>
          <Link
            to="/contact"
            data-testid="blog-cta-contact"
            className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-block"
          >
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;